
import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Sparkles, Loader2, Bot } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";
import { useApp } from '../App';

const LuminaAI: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{role: 'user' | 'ai', text: string}[]>([
    { role: 'ai', text: 'Merhaba! Ben Lumina AI. Size en uygun mumlarımızı seçmenize veya siparişinizle ilgili sorularınıza yardımcı olabilirim. Nasıl yardımcı olabilirim?' }
  ]);
  const [loading, setLoading] = useState(false);
  const { products, categories } = useApp();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const productContext = products.map(p => `${p.name} (${p.category}): ${p.price} TL. ${p.description}`).join('\n');
      
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: userMessage,
        config: {
          systemInstruction: `Sen Lumina Home mağazasının akıllı asistanısın. 
          Mağaza Bilgileri: El yapımı mumlar, biblolar ve dekoratif objeler satıyoruz.
          Kargo: 250 TL üzeri ücretsiz, altı için admin tarafından belirlenen sabit ücret alınır.
          Ürünler: ${productContext}
          Kategoriler: ${categories.map(c => c.name).join(', ')}
          Kurallar: Kibar, yardımsever ve butik bir marka dilinde konuş. Sorulara kısa ve öz yanıtlar ver.`,
        },
      });

      const aiText = response.text || "Şu an size yardımcı olamıyorum, lütfen daha sonra tekrar deneyin.";
      setMessages(prev => [...prev, { role: 'ai', text: aiText }]);
    } catch (error) {
      console.error("AI Error:", error);
      setMessages(prev => [...prev, { role: 'ai', text: "Üzgünüm, bir bağlantı hatası oluştu. Lütfen tekrar deneyin." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* AI Toggle Button */}
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-28 right-8 z-[90] bg-stone-900 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform active:scale-95 flex items-center group"
      >
        <Sparkles size={24} className="text-amber-400" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-8 right-8 z-[100] w-[90vw] md:w-[400px] h-[500px] bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-stone-100 flex flex-col overflow-hidden animate-chat">
          <div className="p-6 bg-stone-900 text-white flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-800 rounded-xl flex items-center justify-center">
                <Bot size={24} />
              </div>
              <div>
                <p className="font-bold text-sm">Lumina AI</p>
                <p className="text-[10px] text-amber-400 font-black uppercase tracking-widest">Akıllı Asistan</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:rotate-90 transition-transform">
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-hide bg-[#FAF8F5]">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-4 rounded-2xl text-sm font-medium leading-relaxed ${
                  msg.role === 'user' 
                  ? 'bg-amber-800 text-white rounded-tr-none' 
                  : 'bg-white text-stone-700 shadow-sm border border-stone-100 rounded-tl-none'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white p-4 rounded-2xl rounded-tl-none border border-stone-100 shadow-sm">
                  <Loader2 size={20} className="animate-spin text-amber-800" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSend} className="p-4 border-t border-stone-50 bg-white">
            <div className="relative">
              <input 
                type="text"
                placeholder="Bir şeyler sorun..."
                value={input}
                onChange={e => setInput(e.target.value)}
                className="w-full bg-stone-50 border border-stone-100 rounded-xl py-3 pl-4 pr-12 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium"
              />
              <button 
                type="submit"
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-stone-900 text-white rounded-lg flex items-center justify-center hover:bg-amber-800 transition-colors disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default LuminaAI;
